import { FC, ReactNode, useRef, useState } from 'react'
import classNames from 'classnames'
import useOutsideClick from 'src/hooks/useOutsideClick'
import s from './dashboard.module.scss'
import { CheckIcon, ChevronDown, SearchIcon } from './icons'

export interface PillOption {
  disabled?: boolean
  group?: string
  label: string
  value: string
}

interface Props {
  // Which edge the menu lines up with. Pills at a card's left edge use 'left'
  // so the menu doesn't hang out of the card under the sidebar.
  align?: 'left' | 'right'
  // "Metric", "Platform" … shown before the value.
  label: string
  onChange: (value: string) => void
  options: PillOption[]
  searchable?: boolean
  value: string
  // Rendered instead of the option label inside the pill, e.g. a logo.
  valueSuffix?: ReactNode
}

// The rounded "Metric: Clicks ⌄" dropdown from the design.
export const PillSelect: FC<Props> = ({
  align = 'right',
  label,
  onChange,
  options,
  searchable,
  value,
  valueSuffix,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  useOutsideClick({ onOutsideClick: () => setOpen(false), ref })

  const current = options.find((o) => o.value === value)
  const shown = options.filter((o) =>
    o.label.toLowerCase().includes(q.trim().toLowerCase())
  )
  const groups = Array.from(new Set(shown.map((o) => o.group ?? '')))

  return (
    <div className={s.pillSelect} ref={ref}>
      <button
        aria-expanded={open}
        className={classNames(s.pillBtn, { [s.pillOpen]: open })}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className={s.pillLabel}>{label}:</span>
        <strong>{current?.label ?? '–'}</strong>
        {valueSuffix}
        <ChevronDown className={s.pillChevron} />
      </button>
      {open && (
        <div className={classNames(s.menu, { [s.menuLeft]: align === 'left' })}>
          {searchable && (
            <label className={s.menuSearch}>
              <SearchIcon />
              <input
                autoFocus
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search metrics..."
                value={q}
              />
            </label>
          )}
          <div className={s.menuList}>
            {groups.map((g) => (
              <div key={g}>
                {g && <div className={s.menuGroup}>{g}</div>}
                {shown
                  .filter((o) => (o.group ?? '') === g)
                  .map((o) => (
                    <button
                      className={classNames(s.menuItem, {
                        [s.menuItemActive]: o.value === value,
                      })}
                      disabled={o.disabled}
                      key={o.value}
                      onClick={() => {
                        onChange(o.value)
                        setOpen(false)
                        setQ('')
                      }}
                      type="button"
                    >
                      {o.label}
                      {o.value === value && <CheckIcon />}
                    </button>
                  ))}
              </div>
            ))}
            {!shown.length && <div className={s.menuEmpty}>No match</div>}
          </div>
        </div>
      )}
    </div>
  )
}
